import CircledBackButton from "@/features/Common/components/CircledBackButton";
import { defaultFullscreenPageStyling, panelStyle } from "@/theme";
import { ChevronNextIcon } from "@/theme/icons";
import {
  convertPxToRem,
  formatAmount,
  getDiscountedAmountInCents,
  mapErrorToMessage,
  replaceCentsFromAmount,
} from "@/utils";
import { Box, Divider, Stack, Typography } from "@mui/material";
import React, { PropsWithChildren, useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import IconLoader from "../IconLoader";
import {
  ApplyVoucherResponse,
  DiscountVoucherDetails,
  MemberServiceOfferingUsage,
} from "@/services/core-api-adapter";
import DiscountVoucherSlideUpModal from "../DiscountVoucherSlideUpModal";
import {
  FeatureToggleIdentifier,
  isFeatureEnabled,
} from "@/services/feature-toggle-adapter";
import { useSearchParams } from "react-router-dom";
import ButtonWithAnalytics from "../ButtonWithAnalytics";
import DiscountVoucherCaptureModal, {
  CurrentPath,
} from "../DiscountVoucherCaptureModal";

function PaymentOptionCard({
  title,
  onClick,
  icon,
  badgeContent,
  shouldShowBadge,
  children,
}: PropsWithChildren<{
  title: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  badgeContent?: string;
  shouldShowBadge?: boolean;
}>) {
  return (
    <Stack
      aria-label={title}
      component="button"
      sx={{
        ...panelStyle,
        cursor: "pointer",
        border: "none",
      }}
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      width="100%"
      color="neutral"
      onClick={onClick}
    >
      <Stack width="100%">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <Stack direction={"row"} alignItems="center" spacing={1}>
            {icon}
            <Typography variant="body1" color="inherit" textAlign="left">
              {title}
            </Typography>
          </Stack>
          {badgeContent && shouldShowBadge && (
            <Stack
              sx={{
                backgroundColor: "accent1.main",
                borderRadius: 1,
                alignSelf: "center",
                whiteSpace: "nowrap",
              }}
              padding={0.5}
              marginLeft="auto"
            >
              <Typography
                fontWeight={600}
                variant="body2"
                color="primary.contrastText"
              >
                {badgeContent}
              </Typography>
            </Stack>
          )}
        </Stack>
        {children}
      </Stack>
      <Stack marginLeft={2}>
        <ChevronNextIcon color="primary" />
      </Stack>
    </Stack>
  );
}

interface PaymentOptionSelectionProps {
  onBack: () => void;
  onCardPaymentButtonClick: () => void;
  isBuyCareCardVoucher: boolean;
  onVoucherButtonClick?: () => void;
  onOneVoucherButtonClick: () => void;
  serviceOfferingUsageItems?: MemberServiceOfferingUsage[];
  discountVouchers?: MemberServiceOfferingUsage[];
  selectedService?: {
    chargeCode: string | null;
    amount: string | null;
  };
  onDiscountVoucherSelected?: (
    selectedVoucher: MemberServiceOfferingUsage | null
  ) => void;
  selectedDiscountVoucher?: MemberServiceOfferingUsage | null;
  capturedDiscountVoucherCode?: string | null;
  onApplyDiscountVoucher?: (
    voucherCode: string,
    isUseVoucherLater?: boolean
  ) => void;
  applyDiscountVoucherResponseError?: any;
  applyDiscountVoucherResponse?: ApplyVoucherResponse | null;
  onAddDiscountVoucherClicked?: () => void;
  isUseVoucherLater?: boolean;
}

export default function PaymentOptionSelection({
  onBack,
  onCardPaymentButtonClick,
  isBuyCareCardVoucher,
  onVoucherButtonClick,
  onOneVoucherButtonClick,
  serviceOfferingUsageItems,
  discountVouchers,
  selectedService,
  onDiscountVoucherSelected,
  selectedDiscountVoucher,
  capturedDiscountVoucherCode,
  onApplyDiscountVoucher,
  applyDiscountVoucherResponseError,
  applyDiscountVoucherResponse,
  onAddDiscountVoucherClicked,
  isUseVoucherLater,
}: PaymentOptionSelectionProps) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const voucher = searchParams.get("voucher");

  const [voucherCount, setVoucherCount] = useState<number>(0);
  const [serviceType, setServiceType] = useState<string>("");
  const [discountVoucherDetails, setDiscountVoucherDetails] =
    useState<DiscountVoucherDetails | null>(
      selectedDiscountVoucher?.discountVoucherDetails || null
    );
  const [shouldShowVoucherBadge, setShouldShowVoucherBadge] =
    useState<boolean>(false);
  const [isDiscountVoucherListModalOpen, setIsDiscountVoucherListModalOpen] =
    useState<boolean>(false);
  const [
    isDiscountVoucherCaptureModalOpen,
    setIsDiscountVoucherCaptureModalOpen,
  ] = useState<boolean>(false);
  const [currentVoucherCode, setCurrentVoucherCode] = useState<string>("");

  useEffect(() => {
    if (serviceOfferingUsageItems && serviceOfferingUsageItems?.length > 0) {
      setVoucherCount(serviceOfferingUsageItems?.length);
      setShouldShowVoucherBadge(true);
    }
  }, [serviceOfferingUsageItems]);

  useEffect(() => {
    if (selectedService?.chargeCode === "ONLINE_DOCTOR_CONSULTATION") {
      setServiceType(t("ConsultationOrderSummary.doctorNurse.title"));
    } else if (selectedService?.chargeCode === "DIGITAL_DOCTOR_CONSULT") {
      setServiceType(t("ConsultationOrderSummary.doctor.title"));
    } else
      setServiceType(
        t(`PathologyOrderSummary.${selectedService?.chargeCode}.title` as any)
      );
  }, [selectedService?.chargeCode]);

  useEffect(() => {
    if (
      !!discountVouchers &&
      discountVouchers?.length > 0 &&
      !discountVoucherDetails &&
      !selectedDiscountVoucher
    )
      setIsDiscountVoucherListModalOpen(true);
  }, [discountVouchers, selectedDiscountVoucher]);

  useEffect(() => {
    if (applyDiscountVoucherResponseError?.message) {
      setIsDiscountVoucherCaptureModalOpen(true);
    }
  }, [applyDiscountVoucherResponseError]);

  useEffect(() => {
    if (applyDiscountVoucherResponse?.memberOfferingUsage) {
      setDiscountVoucherDetails(
        applyDiscountVoucherResponse?.memberOfferingUsage
          ?.discountVoucherDetails
      );
    }
  }, [applyDiscountVoucherResponse]);

  const errorMessage = useMemo(() => {
    return mapErrorToMessage(applyDiscountVoucherResponseError, t);
  }, [applyDiscountVoucherResponseError, t]);

  return (
    <React.Fragment>
      <Stack
        color="neutral.dark"
        sx={{
          ...defaultFullscreenPageStyling,
          minHeight: "100%",
          justifyContent: "space-between",
          flex: 1,
        }}
      >
        <Stack p={2} spacing={2}>
          <Stack>
            <CircledBackButton showLabel={true} onClick={onBack} />
          </Stack>
          <Stack>
            <Stack spacing={2} textAlign="center" alignItems="center">
              <IconLoader
                icon="TagWithCheckIcon"
                color="primary"
                sx={{ fontSize: convertPxToRem(90) }}
              />
              <Typography variant="h2" component="h1" color="inherit">
                {t("PaymentOptionSelection.title")}
              </Typography>
              <Typography variant="body1" color="neutral.main">
                {t("PaymentOptionSelection.subtitle")}
              </Typography>
            </Stack>
            {discountVoucherDetails && (
              <Stack
                direction="row"
                justifyContent="left"
                alignItems="center"
                spacing={1}
                borderRadius={1}
                padding={1}
                marginTop={2}
                sx={{
                  backgroundColor: "accent2.light",
                }}
              >
                <IconLoader
                  icon="CheckVerifiedIcon"
                  color="primary"
                  sx={{ fontSize: convertPxToRem(20) }}
                />
                <Typography
                  fontWeight="500"
                  color="neutral.700"
                  textAlign="left"
                  fontSize={12}
                >
                  {t(
                    isUseVoucherLater
                      ? "PaymentOptionSelection.discount.textV2"
                      : "PaymentOptionSelection.discount.text",
                    {
                      amount: formatAmount(
                        discountVoucherDetails?.discountAmount || 0,
                        discountVoucherDetails?.currencyCode || "ZAR"
                      ),
                    }
                  )}
                </Typography>
              </Stack>
            )}
            {(discountVoucherDetails || selectedService) &&
              isFeatureEnabled(
                FeatureToggleIdentifier.ENABLE_DISCOUNT_VOUCHERS
              ) && (
                <Stack
                  spacing={2}
                  padding={2}
                  pt={5}
                  width="100%"
                  sx={{
                    ...panelStyle,
                    marginTop: 2,
                    paddingTop: convertPxToRem(20),
                  }}
                >
                  <Typography variant="h4" fontWeight={600}>
                    {t("OrderSummary.PanelTwo.title")}
                  </Typography>
                  <Divider />
                  <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-between"
                  >
                    <Typography variant="h4" fontWeight={400}>
                      {serviceType}
                    </Typography>
                    <Typography color="primary">
                      {discountVoucherDetails?.originalAmount
                        ? formatAmount(
                            discountVoucherDetails?.originalAmount || 0,
                            discountVoucherDetails?.currencyCode || "ZAR"
                          )
                        : replaceCentsFromAmount(
                            selectedService?.amount || "0"
                          )}
                    </Typography>
                  </Box>
                  {discountVoucherDetails?.discountAmount &&
                    !isUseVoucherLater && (
                      <Box
                        display="flex"
                        flexDirection="row"
                        justifyContent="space-between"
                      >
                        <Typography variant="h4" fontWeight={400}>
                          {t("OrderSummary.discountVoucherApplied.text")}
                        </Typography>
                        <Typography color="primary">
                          {`-${formatAmount(
                            discountVoucherDetails?.discountAmount,
                            discountVoucherDetails?.currencyCode
                          )}`}
                        </Typography>
                      </Box>
                    )}
                  <Divider sx={{ width: "100%" }} />
                  <Stack
                    aria-label={t("OrderSummary.PanelTwo.totalText")}
                    direction={"row"}
                    justifyContent="space-between"
                  >
                    <Typography variant="h4">
                      {t("OrderSummary.PanelTwo.totalText")}
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{ fontSize: convertPxToRem(26) }}
                      color="primary"
                    >
                      {discountVoucherDetails?.discountAmount &&
                      !isUseVoucherLater
                        ? formatAmount(
                            getDiscountedAmountInCents(
                              selectedService?.amount || 0,
                              discountVoucherDetails?.discountAmount
                            ),
                            discountVoucherDetails?.currencyCode || "ZAR"
                          )
                        : replaceCentsFromAmount(
                            selectedService?.amount || "0"
                          )}
                    </Typography>
                  </Stack>
                </Stack>
              )}

            {isFeatureEnabled(
              FeatureToggleIdentifier.ENABLE_DISCOUNT_VOUCHERS
            ) &&
              (isUseVoucherLater || !discountVoucherDetails) && (
                <Stack mt={2}>
                  <ButtonWithAnalytics
                    intent="confirmational"
                    page="DiscountVoucherCaptureModal"
                    text={t("More.myDiscountVouchers.CTA.text")}
                    fullWidth
                    variant="outlined"
                    onClick={() => {
                      setIsDiscountVoucherListModalOpen(false);
                      setIsDiscountVoucherCaptureModalOpen(true);
                      onAddDiscountVoucherClicked?.();
                    }}
                  >
                    {t("More.myDiscountVouchers.CTA.text")}
                  </ButtonWithAnalytics>
                </Stack>
              )}

            <Stack spacing={2} py={2}>
              <PaymentOptionCard
                title={t("PaymentOptionSelection.payWithCard.title")}
                onClick={onCardPaymentButtonClick}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <IconLoader
                    icon="VisaCardIcon"
                    sx={{
                      width: convertPxToRem(34),
                      height: convertPxToRem(34),
                    }}
                  />
                  <IconLoader
                    icon="MasterCardIcon"
                    sx={{
                      width: convertPxToRem(35),
                      height: convertPxToRem(34),
                    }}
                  />
                  <IconLoader
                    icon="CapitecIcon"
                    sx={{
                      width: convertPxToRem(44),
                      height: convertPxToRem(34),
                    }}
                  />
                  <IconLoader
                    icon="EFTIcon"
                    sx={{
                      width: convertPxToRem(35),
                      height: convertPxToRem(34),
                    }}
                  />
                </Stack>
              </PaymentOptionCard>
              {!isBuyCareCardVoucher && !discountVoucherDetails && (
                <PaymentOptionCard
                  title={t("PaymentOptionSelection.payWithCareCard.title")}
                  onClick={onVoucherButtonClick}
                  icon={<IconLoader icon="UnuHealthIcon" color="primary" />}
                  shouldShowBadge={shouldShowVoucherBadge}
                  badgeContent={t(
                    "PaymentOptionSelection.payWithCareCard.voucherCount",
                    {
                      number: voucherCount,
                    }
                  )}
                />
              )}
              <PaymentOptionCard
                title={t("PaymentOptionSelection.payWithOneVoucher.title")}
                onClick={onOneVoucherButtonClick}
                icon={<IconLoader icon="OneVoucherIcon" />}
              />
            </Stack>
          </Stack>
        </Stack>
      </Stack>

      {!!discountVouchers &&
        discountVouchers?.length > 0 &&
        !isBuyCareCardVoucher &&
        !voucher &&
        !selectedDiscountVoucher &&
        !applyDiscountVoucherResponseError &&
        !applyDiscountVoucherResponse &&
        !isDiscountVoucherCaptureModalOpen &&
        isFeatureEnabled(FeatureToggleIdentifier.ENABLE_DISCOUNT_VOUCHERS) && (
          <DiscountVoucherSlideUpModal
            isModalOpen={isDiscountVoucherListModalOpen}
            onModalClose={() => setIsDiscountVoucherListModalOpen(false)}
            discountVouchers={discountVouchers}
            onContinueButtonClick={(
              selectedDiscountVoucher: MemberServiceOfferingUsage | null
            ) => {
              setIsDiscountVoucherListModalOpen(false);
              setDiscountVoucherDetails(
                selectedDiscountVoucher?.discountVoucherDetails ?? null
              );
              onDiscountVoucherSelected?.(selectedDiscountVoucher);
            }}
          />
        )}

      {isDiscountVoucherCaptureModalOpen &&
        isFeatureEnabled(FeatureToggleIdentifier.ENABLE_DISCOUNT_VOUCHERS) && (
          <DiscountVoucherCaptureModal
            isModalOpen={isDiscountVoucherCaptureModalOpen}
            onModalClose={() => {
              setIsDiscountVoucherCaptureModalOpen(false);
              setIsDiscountVoucherListModalOpen(false);
            }}
            errorMessage={errorMessage}
            currentPath={CurrentPath.PaymentOptions}
            currentVoucherCode={
              currentVoucherCode || capturedDiscountVoucherCode || ""
            }
            handleApplyVoucher={async (
              voucherCode: string,
              isUseVoucherLater?: boolean
            ) => {
              setCurrentVoucherCode(voucherCode);
              onApplyDiscountVoucher?.(voucherCode, isUseVoucherLater);
            }}
          />
        )}
    </React.Fragment>
  );
}
